import { type EventParams, type CustomParams } from 'firebase/analytics';
import { pickBy } from 'lodash-es';
import queryString from 'query-string';
import snakeCaseKeys from 'snakecase-keys';

import { tracker } from '@/utils/firebase';

type EventName = 'parents_web_pageviews' | 'parents_web_announcement_view';

interface EventTrackingContext {
  parentId?: number;
  pathname: string;
  query: string;
  fragment: string;
}

// URL のパスやクエリにトークン・団体会員コードが入る画面があるので、GA に送る前に除く
const SENSITIVE_PATH_PATTERNS: [RegExp, string][] = [
  [/(\/email_change_tokens\/)[^/]+/i, '$1:token'],
  [
    /(\/mypage\/invite\/)(?!(?:inquiry|limit-error|complete)\/?$)[^/]+/i,
    '$1:code',
  ],
];

const maskPathname = (pathname: string) =>
  SENSITIVE_PATH_PATTERNS.reduce(
    (masked, [pattern, replacement]) => masked.replace(pattern, replacement),
    pathname,
  );

const pickUtmQuery = (query: string) => {
  const utmParams = pickBy(queryString.parse(query, { sort: false }), (_, k) =>
    k.startsWith('utm_'),
  );
  const utmQuery = queryString.stringify(utmParams, { sort: false });
  return utmQuery ? `?${utmQuery}` : '';
};

class EventTracker {
  private static trackingContext?: EventTrackingContext;

  public static pageview() {
    this.track('parents_web_pageviews', this.pickUtmParams());
  }

  public static announcementView(eventParams: CustomParams) {
    this.track('parents_web_announcement_view', snakeCaseKeys(eventParams));
  }

  public static updateContext(context?: EventTrackingContext) {
    this.trackingContext = context && {
      ...context,
      pathname: maskPathname(context.pathname),
      query: pickUtmQuery(context.query),
    };
  }

  private static track(eventName: EventName, eventParams?: CustomParams) {
    tracker.trackEvent(this.trackingContext?.parentId?.toString(), eventName, {
      ...eventParams,
      ...this.buildCommonParameters(),
    });
  }

  private static buildCommonParameters(): EventParams {
    return {
      build_id: import.meta.env.VITE_BUILD_VERSION,
      pathname: this.trackingContext?.pathname,
      query_string: this.trackingContext?.query,
      fragment: this.trackingContext?.fragment,
      parent_id: this.trackingContext?.parentId,
    };
  }

  private static pickUtmParams(): CustomParams {
    const queryParams = queryString.parse(this.trackingContext?.query || '');
    return pickBy(queryParams || {}, (_, k) => k.startsWith('utm_'));
  }
}

export default EventTracker;
